/**
 * Shared helpers for preserving the form a student typed (fraction vs decimal)
 * alongside the parsed numeric value, so display surfaces can render the
 * original input rather than a normalized form.
 */

/**
 * Parse a string that may be a fraction ("1/2", "-3/4") or a decimal/integer.
 * Returns { valid, value, display } where `display` is the trimmed input,
 * normalized so fractions have no inner spaces (e.g. " 1 / 2 " → "1/2").
 */
export const parseInputWithLabel = (str) => {
  if (!str || typeof str !== 'string') {
    return { valid: false, error: 'Invalid input' };
  }
  const trimmed = str.trim();
  if (!trimmed) {
    return { valid: false, error: 'Empty value' };
  }

  const fractionMatch = trimmed.match(/^(-?\d+(?:\.\d+)?)\s*\/\s*(-?\d+(?:\.\d+)?)$/);
  if (fractionMatch) {
    const numerator = parseFloat(fractionMatch[1]);
    const denominator = parseFloat(fractionMatch[2]);
    if (denominator === 0) {
      return { valid: false, error: 'Division by zero' };
    }
    return {
      valid: true,
      value: numerator / denominator,
      display: `${fractionMatch[1]}/${fractionMatch[2]}`,
    };
  }

  const num = parseFloat(trimmed);
  if (isNaN(num) || !/^-?\d+(?:\.\d+)?$/.test(trimmed)) {
    return { valid: false, error: `Invalid number: "${trimmed}"` };
  }
  return { valid: true, value: num, display: trimmed };
};

/**
 * Render helper: prefer the stored label, fall back to a numeric formatter.
 */
export const pickLabel = (label, value, formatter) =>
  label != null && label !== '' ? label : formatter(value);

/**
 * After snap/round, decide whether the stored label still matches the numeric.
 * Returns true if the label parses back to the same numeric within epsilon.
 */
export const labelStillValid = (label, snappedValue, epsilon = 0.0001) => {
  if (label == null) return false;
  const parsed = parseInputWithLabel(label);
  if (!parsed.valid) return false;
  return Math.abs(parsed.value - snappedValue) < epsilon;
};
